import React, { useEffect, useMemo } from 'react';
import { useAppSelector } from 'hooks';
import ReactSelect from 'react-select';

import dayjs, { formatDateString } from '~libs/dayjs';

import { InputProps } from '~components/form-fields';
import { Option } from '~components/form-fields/inputs/Select';

import { SahBudget } from '~support-at-home/models';

import { selectClientPackageScheduleByPackageId } from '~main/selectors/clients/clientPackageSchedules';
import { selectSahBudgets } from '~support-at-home/selectors/sahBudgets';

const sahBudgetCoversRange = (sah: SahBudget, tpStart: string, tpEnd: string) => {
  if (!sah.start_date || !sah.end_date) return false;
  const sahStart = dayjs(sah.start_date);
  const sahEnd = dayjs(sah.end_date);
  return (sahStart.isSame(tpStart) || sahStart.isBefore(tpStart)) && (sahEnd.isSame(tpEnd) || sahEnd.isAfter(tpEnd));
};

const SahBudgetSelect: React.FC<InputProps> = ({ value, onChange, id, disabled }) => {
  const { client_sah_budget_id, start_date, end_date, package_fixed_id } = value;

  const sahBudgets = useAppSelector(selectSahBudgets);
  const packageSchedule = useAppSelector((state) => selectClientPackageScheduleByPackageId(state, package_fixed_id));

  const fundingTypeId = packageSchedule?.package?.funding_type?.id;
  const fundingTypeName = packageSchedule?.package?.funding_type?.name;

  const filteredOptions: Option[] = useMemo(() => {
    if (!fundingTypeId || !start_date || !end_date) return [];

    return sahBudgets
      .filter(
        (sah: SahBudget) => sah.funding_source_id === fundingTypeId && sahBudgetCoversRange(sah, start_date, end_date),
      )
      .map((sah: SahBudget) => ({
        value: sah.id,
        label: `${fundingTypeName} (${formatDateString(sah.start_date)} - ${formatDateString(sah.end_date)})`,
      }));
  }, [sahBudgets, fundingTypeId, fundingTypeName, start_date, end_date]);

  const selectedOption = filteredOptions.find((opt) => opt.value === client_sah_budget_id) ?? null;

  // Clear stale SAH link when the selected budget is no longer in filtered options
  useEffect(() => {
    if (client_sah_budget_id && !selectedOption) {
      onChange?.('client_sah_budget_id', null);
    }
  }, [client_sah_budget_id, selectedOption]);

  const getNoOptionsMessage = () => {
    if (!package_fixed_id) return 'Select a client package first';
    if (!start_date || !end_date) return 'Set budget dates first';
    return 'No matching SAH budgets found';
  };

  const noOptionsMessage = getNoOptionsMessage();

  return (
    <ReactSelect
      id={id}
      name={id}
      value={selectedOption}
      isDisabled={disabled || false}
      isClearable
      options={filteredOptions}
      classNamePrefix="form-input-select"
      onChange={(option: Option | null) => {
        onChange?.('client_sah_budget_id', option?.value ?? null);
      }}
      placeholder={filteredOptions.length === 0 ? noOptionsMessage : 'Select SAH Budget...'}
      noOptionsMessage={() => noOptionsMessage}
    />
  );
};

export default SahBudgetSelect;
